import API from '@/services/API'
import store from '@/store/store'
import AuthenticationService from '@/services/AuthenticationService'

export default {
	/* API actions
	–––––––––––––––––––––––––––––––––––––––––––––––––– */
	async getAll (options) {
		try {
			const response = await API.get('planning/index', {
				params: options,
				headers: {
					Authorization: `Bearer ${store.state.authentication.token}`
				}
			})
			return response.data.plannings
		} catch (err) {
			if (err.response.status === 401) AuthenticationService.logout()
		}
	},
	async get (planningId) {
		try {
			const response = await API.get(`planning/${planningId}`, {
				headers: {
					Authorization: `Bearer ${store.state.authentication.token}`
				}
			})
			const planning = response.data.planning
			// Add topic index to every task
			planning.Tasks.forEach(function (task) {
				const index = planning.Topics.findIndex(topic => topic.id === task.Info.TopicId)
				task.topicIndex = index
			})
			return planning
		} catch (err) {
			if (err.response.status === 401) AuthenticationService.logout()
		}
	},
	async create (planning) {
		try {
			await API.post('planning', planning, {
				headers: {
					Authorization: `Bearer ${store.state.authentication.token}`
				}
			})
			return
		} catch (err) {
			if (err.response.status === 401) AuthenticationService.logout()
		}
	},
	async edit (planning) {
		try {
			await API.put('planning', planning, {
				headers: {
					Authorization: `Bearer ${store.state.authentication.token}`
				}
			})
			return
		} catch (err) {
			if (err.response.status === 401) AuthenticationService.logout()
		}
	},
	async delete (planningId) {
		try {
			await API.delete(`planning/${planningId}`, {
				headers: {
					Authorization: `Bearer ${store.state.authentication.token}`
				}
			})
			return
		} catch (err) {
			if (err.response.status === 401) AuthenticationService.logout()
		}
	},
	async replicate (planningId) {
		try {
			await API.post('planning/replicate', { planningId }, {
				headers: {
					Authorization: `Bearer ${store.state.authentication.token}`
				}
			})
			return
		} catch (err) {
			if (err.response.status === 401) AuthenticationService.logout()
		}
	},
	/* Utilities
	–––––––––––––––––––––––––––––––––––––––––––––––––– */
	// Progress related
	getOpenTasks (tasks) {
		var openTasks = 0
		const currentDate = new Date()
		tasks.forEach(function (task) {
			const taskDate = new Date(task.Info.date)
			if (taskDate <= currentDate) {
				if (task.ChildTasks) {
					task.ChildTasks.forEach(task => { if (!task.Info.done) openTasks++ })
				} else if (!task.Info.done) openTasks++
			}
		})
		return openTasks
	},
	getProgress (tasks) {
		let tasksTotal = tasks.length
		if (tasksTotal === 0) return 0
		let tasksDone = 0
		let tasksAhead = 0
		const currentDate = new Date()
		tasks.forEach(function (task) {
			const taskDate = new Date(task.Info.date)
			if (task.ChildTasks) {
				tasksTotal += (task.ChildTasks.length - 1)
				task.ChildTasks.forEach(task => {
					const taskDate = new Date(task.Info.date)
					if (task.Info.done && taskDate <= currentDate) tasksDone++
					else if (task.Info.done) tasksAhead++
				})
			} else {
				if (task.Info.done && taskDate <= currentDate) tasksDone++
				else if (task.Info.done) tasksAhead++
			}
		})
		const percentageDone = Math.round((tasksDone / tasksTotal) * 100)
		if (percentageDone < this.getGoal(tasks)) return percentageDone
		const percentageTotal = Math.round(((tasksDone + tasksAhead) / tasksTotal) * 100)
		return percentageTotal
	},
	getGoal (tasks) {
		let tasksTotal = tasks.length
		if (tasksTotal === 0) return 0
		let tasksGoal = 0
		const currentDate = new Date()
		tasks.forEach(function (task) {
			const taskDate = new Date(task.Info.date)
			if (task.ChildTasks) {
				tasksTotal += (task.ChildTasks.length - 1)
				task.ChildTasks.forEach(task => {
					const taskDate = new Date(task.Info.date)
					if (taskDate <= currentDate) tasksGoal++
				})
			} else if (taskDate <= currentDate) tasksGoal++
		})
		const percentage = Math.round((tasksGoal / tasksTotal) * 100)
		return percentage
	},
	getAhead (tasks) {
		const tasksTotal = tasks.length
		if (tasksTotal === 0) return 0
		var tasksAhead = 0
		const currentDate = new Date()
		tasks.forEach(function (task) {
			const taskDate = new Date(task.Info.date)
			if (taskDate > currentDate && task.Info.done) tasksAhead++
		})
		const percentage = Math.round((tasksAhead / tasksTotal) * 100)
		return percentage
	},
	getWeekTasks (week, tasks, options) {
		var weekTasks = []
		tasks.forEach(function (task, index) {
			task.index = index
			const oneDay = 24 * 60 * 60 * 1000
			const taskDate = new Date(task.Info.date)
			const weekDate = new Date(week.date)
			const diffDays = Math.round((taskDate.setHours(0, 0, 0, 0) - weekDate.setHours(0, 0, 0, 0)) / oneDay)
			if (diffDays < 7 && diffDays >= 0) weekTasks.push(task)
		})
		// Check if any options have been specified
		if (options) {
			// If unsorted is requested, return now
			if (options.unsorted) return weekTasks
		}
		return weekTasks.sort((a, b) => {
			return new Date(a.Info.date) - new Date(b.Info.date)
		})
	},
	getWeekDeadlines (week, deadlines) {
		var weekDeadlines = []
		deadlines.forEach(function (deadline) {
			const oneDay = 24 * 60 * 60 * 1000
			const deadlineDate = new Date(deadline.date)
			const weekDate = new Date(week.date)
			const diffDays = Math.round((deadlineDate.setHours(0, 0, 0, 0) - weekDate.setHours(0, 0, 0, 0)) / oneDay)
			if (diffDays < 7 && diffDays >= 0) weekDeadlines.push(deadline)
		})
		return weekDeadlines
	},
	getWeekContent (week, tasks, deadlines) {
		const weekTasks = this.getWeekTasks(week, tasks)
		const weekDeadlines = this.getWeekDeadlines(week, deadlines)
		var weekContent = weekTasks.concat(weekDeadlines)
		return weekContent
	},
	getDayTasks (day, tasks) {
		var dayTasks = []
		tasks.forEach(function (task, index) {
			if (new Date(day).setHours(0, 0, 0, 0) === new Date(task.Info.date).setHours(0, 0, 0, 0)) dayTasks.push(task)
		})
		return dayTasks
	},
	getMonthTasks (month, tasks) {
		var monthTasks = []
		tasks.forEach(function (task) {
			const taskDate = new Date(task.Info.date)
			if (taskDate >= month.start && taskDate <= month.end) monthTasks.push(task)
		})
		return monthTasks
	},
	getTopicTasks (tasks, topicIndex) {
		var topicTasks = []
		tasks.forEach(function (task, index) {
			task.index = index
			if (task.topicIndex === topicIndex) topicTasks.push(task)
		})
		return topicTasks
	},
	getTaskTypes (tasks) {
		var taskTypes = []
		tasks.forEach(function (task) {
			if (!taskTypes.includes(task.type)) taskTypes.push(task.type)
		})
		return taskTypes
	},
	getTypeTasks (tasks, type) {
		return tasks.filter(function (task) {
			return task.type === type
		})
	},
	// Returns all tasks that do not fall in between the specified dates
	getOutOfBoundsTasks (start, end, tasks) {
		const startDate = new Date(start)
		const endDate = new Date(end)
		var outOfBoundsTasks = []
		tasks.forEach(function (task) {
			const taskDate = new Date(task.Info.date)
			if (taskDate < startDate || taskDate > endDate) outOfBoundsTasks.push(task)
		})
		return outOfBoundsTasks
	}
}
